import { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  MapPin, Car, Star, Info, Users, Briefcase, Settings2, Snowflake, ChevronDown
} from "lucide-react";
import Form from "../components/Form";

const cx = (...classes) => classes.filter(Boolean).join(" ");

const SpecPill = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-1 text-[10px] font-semibold font-body text-text-muted bg-surface border border-surface-border rounded-full px-1.5 py-1 whitespace-nowrap">
    <Icon size={12} className="text-primary shrink-0" />
    <span>{label}</span>
  </div>
);

const FaqItem = ({ faq, isOpen, onToggle }) => (
  <div className={cx(
    "rounded-xl border overflow-hidden transition-all duration-200",
    isOpen ? "border-primary/30 shadow-card" : "border-surface-border"
  )}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-surface hover:bg-surface-raised transition-colors"
      aria-expanded={isOpen}
    >
      <span className="font-heading font-semibold text-text text-sm sm:text-base">{faq.question}</span>
      <ChevronDown size={16} className={cx("flex-shrink-0 text-primary transition-transform duration-200", isOpen && "rotate-180")} />
    </button>
    {isOpen && (
      <div className="px-6 pb-5 pt-1 bg-surface border-t border-surface-border">
        <p className="font-body text-text-muted text-sm leading-relaxed">{faq.answer}</p>
      </div>
    )}
  </div>
);

const CityView = ({ data }) => {
  const [openFaq, setOpenFaq] = useState(null);

  const section1 = data?.["section-1"];
  const section2 = data?.["section-2"];
  const section3 = data?.["section-3"];
  const seo = data?.seo;

  if (!data || !section1?.city) return (
    <div className="flex items-center justify-center min-h-[400px] text-text-muted font-body">
      Loading city data...
    </div>
  );

  const { countryCode, countryName, city, locationList = [], vehicleList } = section1;
  const { facts, stats, topVendors = [] } = section2 || {};
  const {
    faqTitle,
    description: faqDescription,
    faqsList = [],
    alternativeCities = [],
  } = section3 || {};

  return (
    <div className="min-h-screen mt-12 font-body">
      <Helmet>
        <title>{seo?.title || `Car Rental in ${city?.name} | RentGurus`}</title>
        {seo?.metaDescription && <meta name="description" content={seo.metaDescription} />}
        {seo?.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
      </Helmet>

      {/* ── HERO ── */}
      <section className="relative min-h-[80vh] pt-24 pb-10 sm:pb-14 px-4 sm:px-6">
        <div className="absolute inset-0 z-0">
          <img
            src={city?.hero?.image || "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?q=80&w=2000"}
            alt={city?.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark-deep/85 via-dark-deep/55 to-dark-deep/40" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold font-heading uppercase tracking-wider text-primary bg-white/10 border border-white/20 rounded-full px-3 py-1.5">
            <MapPin size={12} />
            {city?.name}{countryCode ? `, ${countryCode}` : ""}
          </span>

          <h1 className="mt-4 font-heading text-4xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight max-w-4xl">
            Car Rental in {city?.name}
          </h1>

          {city?.hero?.tagline && (
            <p className="mt-4 font-heading text-lg sm:text-xl font-bold text-white/90 max-w-2xl">
              {city.hero.tagline}
            </p>
          )}
          {city?.hero?.description && (
            <p className="mt-2 font-body text-sm sm:text-base text-white/70 max-w-2xl">
              {city.hero.description}
            </p>
          )}
        </div>

        {/* form now lives in normal flow, inside the same image container */}
        <div className="relative z-20 max-w-7xl mx-auto mt-10">
          <Form isHero={true} />
        </div>
      </section>

      {/* ── PICKUP LOCATIONS ── */}
      {locationList.length > 0 && (
        <section className="bg-surface py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-6">
              Pickup locations in {city?.name}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {locationList.map((loc) => (
                <div key={loc._id} className="border border-surface-border rounded-xl p-5 bg-surface">
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-primary shrink-0" />
                    <h3 className="font-heading font-semibold text-text text-sm">{loc.locationName}</h3>
                  </div>
                  {loc.description && (
                    <p className="font-body text-text-muted text-sm mt-2 leading-relaxed">
                      {loc.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── AVAILABLE VEHICLES ── */}
      {vehicleList?.featuredCars?.length > 0 && (
        <section className="bg-surface-raised py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-6">
              {vehicleList.title || `Available Vehicles in ${city?.name}`}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {vehicleList.featuredCars.map((car, i) => (
                <div key={i} className="bg-surface rounded-xl border border-surface-border shadow-card p-4">
                  {/* Fixed-height frame so the image's h-full has something
                      real to fill — without this, h-full has no defined
                      ancestor height to resolve against and the image
                      collapses/expands unpredictably. */}
                  <div className="h-32 mb-3">
                    <img
                      src={car.image}
                      alt={car.name}
                      className="h-full w-full object-contain soft-edge-mask"
                    />
                  </div>
                  <h3 className="font-heading font-bold text-text text-sm leading-tight">
                    {car.name}
                  </h3>
                  <p className="font-body text-text-muted text-xs mb-3">{car.type}</p>

                  <div className="flex flex-nowrap items-center justify-between gap-1">
                    <SpecPill icon={Users} label={car.specs?.seats} />
                    <SpecPill icon={Briefcase} label={car.specs?.bags} />
                    <SpecPill icon={Settings2} label={car.specs?.transmission} />
                    <SpecPill
                      icon={Snowflake}
                      label={car.specs?.airCondition === "Yes" ? "A/C" : "No A/C"}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── DRIVING FACTS + STATS ── */}
      {facts?.factsList?.length > 0 && (
        <section className="bg-surface py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1fr_300px] gap-8">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-2">
                {facts.title}
              </h2>
              {facts.description && (
                <p className="font-body text-text-muted text-sm mb-6 max-w-2xl">
                  {facts.description}
                </p>
              )}
              <div className="flex flex-col gap-3">
                {facts.factsList.map((fact, i) => (
                  <div key={i} className="border border-surface-border rounded-xl p-4 bg-surface">
                    <div className="flex items-center gap-2">
                      <Info size={16} className="text-primary shrink-0" />
                      <h4 className="font-heading font-semibold text-text text-sm">{fact.title}</h4>
                    </div>
                    <p className="font-body text-text-muted text-sm mt-1.5 leading-relaxed">
                      {fact.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {stats && (
              <div className="flex flex-col gap-4">
                {stats.avgDailyRate && (
                  <div className="bg-dark-deep rounded-xl p-5">
                    <p className="font-heading text-[10px] font-bold uppercase tracking-widest text-white/60">
                      Average Daily Rate
                    </p>
                    <p className="font-heading text-3xl font-bold text-primary mt-1">
                      {stats.avgDailyRate}
                    </p>
                  </div>
                )}
                {typeof stats.vendorCount === "number" && (
                  <div className="border border-surface-border rounded-xl p-5 bg-surface">
                    <p className="font-heading text-[10px] font-bold uppercase tracking-widest text-text-muted">
                      Vendors Available
                    </p>
                    <p className="font-heading text-3xl font-bold text-text mt-1">
                      {stats.vendorCount}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── TOP VENDORS ── */}
      {topVendors.length > 0 && (
        <section className="bg-surface-raised py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-6">
              Top rental vendors in {city?.name}
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {topVendors.map((vendor, i) => (
                <div
                  key={i}
                  className="bg-surface rounded-xl border border-surface-border shadow-card p-4 flex items-center gap-3"
                >
                  <div className="w-16 h-10 rounded-md flex items-center justify-center overflow-hidden shrink-0">
                    {vendor.logo ? (
                      <img src={vendor.logo} alt={vendor.name} className="max-w-full max-h-full object-contain" />
                    ) : (
                      <Car size={20} className="text-primary" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-text text-sm truncate">{vendor.name}</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Star size={13} className="fill-primary text-primary" />
                      <span className="font-heading text-xs font-semibold text-text">{vendor.rating}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQs ── */}
      {faqsList.length > 0 && (
        <section className="bg-surface py-12 sm:py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center mb-10">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-text mb-2">
              {faqTitle || `Frequently asked questions about ${city?.name}`}
            </h2>
            {faqDescription && (
              <p className="font-body text-text-muted text-sm">{faqDescription}</p>
            )}
          </div>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-3">
            {faqsList.map((faq, i) => (
              <FaqItem
                key={i}
                faq={faq}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              />
            ))}
          </div>

          {alternativeCities.length > 0 && (
            <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-10 text-center">
              <p className="font-heading text-[10px] font-bold uppercase tracking-widest text-text-muted mb-3">
                Car rental in other cities
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                {alternativeCities.map((c) => (
                  // City pages are keyed by the CMS's own path
                  // ("/rent-a-car/{countryName}-{citySlug}", lowercased —
                  // confirmed from this page's own data, e.g.
                  // "/rent-a-car/canada-toronto"). alternativeCities only
                  // carries {name, slug}, no per-city country, so this
                  // still assumes the alternative city is in the SAME
                  // country as the current page (countryName above) —
                  // true for Toronto→Mississauga/Ottawa, but confirm with
                  // whoever built the portal before relying on it for a
                  // list that could span countries.
                  <a
                    key={c.slug}
                    href={`/rent-a-car/${(countryName || "").toLowerCase()}-${c.slug}`}
                    className="text-xs font-bold font-heading text-text border border-surface-border rounded-full px-4 py-2 hover:border-primary hover:text-primary transition-colors"
                  >
                    Car rental in {c.name}
                  </a>
                ))}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default CityView;
